import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ProductCollectionOverlay from './ProductCollectionOverlay';

// Etsy Badge Component
const EtsyBadgeWrapper = styled(motion.a)`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  margin: 2.2rem auto 0;
  cursor: pointer;
  outline: none !important;
  -webkit-tap-highlight-color: transparent;
  
  &:hover, &:focus, &:active, &:focus-visible {
    text-decoration: none;
    outline: none !important;
    box-shadow: none !important;
  }
`;

const EtsyBadgeImg = styled.img`
  width: 175px;
  height: 175px;
  object-fit: contain;
  filter: drop-shadow(0 12px 28px rgba(0, 0, 0, 0.35));
  transition: transform 0.3s ease, filter 0.3s ease;
  
  ${EtsyBadgeWrapper}:hover & {
    filter: drop-shadow(0 16px 36px rgba(241, 100, 30, 0.55));
  }
  
  @media (max-width: 768px) {
    width: 145px;
    height: 145px;
  }
`;

const EtsyBadgeLabel = styled.span`
  margin-top: 0.8rem;
  background: linear-gradient(135deg, #f1641e 0%, #ff8c42 100%);
  color: white;
  padding: 0.5rem 1.3rem;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: bold;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  letter-spacing: 0.4px;
  box-shadow: 0 4px 14px rgba(241, 100, 30, 0.4);
  border: 2px solid #ffffff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.3s ease;
  
  ${EtsyBadgeWrapper}:hover & {
    box-shadow: 0 6px 20px rgba(241, 100, 30, 0.65);
    background: linear-gradient(135deg, #e05510 0%, #ff7a29 100%);
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.4rem 1.1rem;
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 1rem 2rem;
  padding-top: 3rem;
  position: relative;
  overflow: visible;
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    padding-top: 4rem;
    min-height: 90vh;
  }
`;

// Audio player components - commented out for future use
/*
const HangingBanner = styled(motion.div)`
  position: fixed;
  top: 85px;
  left: 10px;
  z-index: 999;
  
  @media (max-width: 768px) {
    top: 75px;
    left: 8px;
  }
`;

const BannerRibbon = styled.div`
  width: 80px;
  height: 45px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 50%, #6bcf7f 100%);
  border-radius: 0 0 12px 12px;
  position: relative;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  border: 2px solid #fff;
  border-top: none;
  
  @media (max-width: 768px) {
    width: 70px;
    height: 40px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid #ff6b6b;
  }
`;

const MusicNote = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.8rem;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const AudioPlayerPopup = styled(motion.div)`
  position: fixed;
  top: 140px;
  left: 10px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 50%, #6bcf7f 100%);
  border-radius: 15px;
  padding: 1rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  border: 3px solid #fff;
  z-index: 1000;
  min-width: 250px;
  
  @media (max-width: 768px) {
    top: 130px;
    left: 8px;
    min-width: 220px;
    padding: 0.8rem;
  }
`;

const AudioControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const ControlButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  color: #333;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1rem;
  }
`;

const TrackInfo = styled.div`
  color: white;
  font-size: 0.9rem;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  text-align: center;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const HiddenAudio = styled.audio`
  display: none;
`;
*/

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  z-index: 2;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 0.5rem;
    margin-top: 1rem;
  }
`;

const HeroLogo = styled(motion.div)`
  margin: 1rem 0 2rem 0;
  
  @media (max-width: 768px) {
    margin: 0.5rem 0 1.5rem 0;
  }
  
  img {
    max-width: 400px;
    height: auto;
    filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3));
    
    @media (max-width: 768px) {
      max-width: 320px;
    }
    
    @media (max-width: 480px) {
      max-width: 280px;
    }
  }
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(45deg, #6bcf7f, #4caf50);
  color: white;
  padding: 1.5rem 2rem;
  border-radius: 15px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 25px rgba(107, 207, 127, 0.4);
  text-align: center;
  
  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.5rem;
  }
  
  p {
    margin: 0;
    font-size: 1.1rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(motion.a)`
  display: inline-block;
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.3rem;
  font-weight: bold;
  box-shadow: 0 8px 25px rgba(255, 107, 107, 0.4);
  transition: all 0.3s ease;
  margin: 0 1rem;
  
  @media (max-width: 768px) {
    padding: 0.8rem 1.5rem;
    font-size: 1.1rem;
    margin: 0.5rem;
    display: block;
    width: 80%;
    max-width: 280px;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 1.2rem;
    font-size: 1rem;
    width: 90%;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 35px rgba(255, 107, 107, 0.5);
  }
`;

const FloatingChickens = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  
  @media (max-width: 768px) {
    /* Keep floating chickens visible on mobile but adjust positioning */
    display: block;
  }
`;

const FloatingChicken = styled(motion.img)`
  position: absolute;
  width: 80px;
  height: auto;
  opacity: 0.6;
  
  @media (max-width: 768px) {
    width: 50px;
    opacity: 0.4;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    opacity: 0.3;
  }
`;

const SpeechBubble = styled(motion.div)`
  position: fixed;
  background: white;
  border: 3px solid #ff6b6b;
  border-radius: 20px;
  padding: 15px 20px;
  max-width: 200px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  font-size: 0.9rem;
  font-weight: bold;
  color: #333;
  text-align: center;
  z-index: 3000;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid #ff6b6b;
  }
  
  &::before {
    content: '';
    position: absolute;
    bottom: -7px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid white;
  }
`;

const Hero = ({ onShopClick }) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showProductCollection, setShowProductCollection] = useState(false);
  const [showSpeechBubble, setShowSpeechBubble] = useState(false);
  const [currentSaying, setCurrentSaying] = useState('');
  const [bubblePosition, setBubblePosition] = useState({ x: 0, y: 0 });
  // Audio player state - commented out for future use
  // const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  // const [isPlaying, setIsPlaying] = useState(false);
  // const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  // const [audioRef, setAudioRef] = useState(null);

  // Array of random sayings
  const randomSayings = [
    "Cluck yeah! 🐔",
    "You're egg-cellent! 🥚",
    "Time to get clucky! 🐣",
    "Feeling peckish? 🍗",
    "Let's wing it! 🦅",
    "You're the best! 🐓",
    "Stay egg-cited! 🥚",
    "Cluckin' awesome! 🐔",
    "You're egg-stra special! 🥚",
    "Let's get clucky! 🐣",
    "You're egg-actly right! 🥚",
    "Cluck yeah, you rock! 🐔",
    "Stay egg-cited! 🥚",
    "You're egg-straordinary! 🥚",
    "Cluckin' fantastic! 🐔",
    "Henpecked just for you! 🐔",
    "Cluckin ray of sunshine! ☀️",
    "What the cluck! 🤔",
    "I don't give a cluck! 😤",
    "You dumb cluck! 🤪",
    "Peckerhead! 🐓",
    "Stop cluckin' around! ⏰",
    "We're bawked! 🐔",
    "Hen can we meet you? 🤝",
    "Something to cluck/crow about! 🎉",
    "Have to break an egg to make an omelet! 🍳",
    "Guess What…..Chikin Butt! 🍑",
    "Guess Who…….Chikin Poo! 💩",
    "Guess Why…….Chikin Thigh! 🍗",
    "The sky is falling! ☁️",
    "Fly the coop! 🦅",
    "Fowl around and find out! ⚠️",
    "Tom Fowlery! 🐓",
    "Us Ladies, nobody lays'em better than us! 👑",
    "Where's my cluckin' coffee! ☕",
    "No fowl language, Please! 🤐",
    "Welcome to our coop, we are all cluckin crazy! 🏠",
    "My spirit animal is a grumpy chikin! 😠",
    "Crazy Chikin Lady parking only, violators will be plucked and roasted! 🚗",
    "I can rise and shine, but not at the same time! 🌅",
    "Let me pour you a tall glass of Get Over It, and here is a straw so you can suck it up! 🥤"
  ];

  // Array of available sound files
  const soundFiles = [
    '/assets/chikinSOUND1.mp3',
    '/assets/chikinSOUND2.mp3',
    '/assets/chikinSOUND3.mp3',
    '/assets/chikinSOUND4.mp3',
    '/assets/chikinSOUND5.mp3',
    '/assets/chikinSOUND6.mp3'
  ];

  // Array of music files - using relative paths for production
  const musicFiles = useMemo(() => [
    '/assets/music/Chikin Lady Main Theme.mp3',
    '/assets/music/Cluckin Awesome 1.mp3',
    '/assets/music/Cluckin Awesome 2.mp3',
    '/assets/music/Cluckin Awesome 3.mp3',
    '/assets/music/Cluckin Awesome 4.mp3',
    '/assets/music/Cluckin Awesome 5.mp3',
    '/assets/music/Cluckin Awesome 6.mp3',
    '/assets/music/Cluckin Awesome 7.mp3',
    '/assets/music/Cluckin Awesome 8.mp3',
    '/assets/music/Cluckin Awesome 9.mp3',
    '/assets/music/Cluckin Awesome 10.mp3',
    '/assets/music/Cluckin Awesome 11.mp3',
    '/assets/music/Cluckin Awesome 11 remix v1.mp3',
    '/assets/music/Cluckin Awesome 11 remix v2.mp3',
    '/assets/music/Fall - Chikin Lady.mp3'
  ], []);

  // Audio player functions - commented out for future use
  // const handleBannerClick = () => { ... };
  // const togglePlayPause = () => { ... };
  // const playNextTrack = () => { ... };
  // const handleAudioEnded = () => { ... };
  // const handleAudioLoaded = () => { ... };
  // const handleAudioError = (error) => { ... };

  // Debug function to test file accessibility
  const testAudioFile = (url) => {
    const audio = new Audio();
    audio.oncanplaythrough = () => console.log('File accessible:', url);
    audio.onerror = () => console.log('File not accessible:', url);
    audio.src = url;
  };

  // Function to play a random sound and show speech bubble
  const handleChickenClick = (event) => {
    // Play random sound
    const randomIndex = Math.floor(Math.random() * soundFiles.length);
    const audio = new Audio(soundFiles[randomIndex]);
    audio.play().catch(error => {
      console.log('Audio playback failed:', error);
    });

    // Get random saying
    const randomSayingIndex = Math.floor(Math.random() * randomSayings.length);
    const newSaying = randomSayings[randomSayingIndex];
    setCurrentSaying(newSaying);

    // Calculate bubble position relative to the clicked chicken
    const rect = event.currentTarget.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    const x = rect.left + rect.width / 2 + (isMobile ? 30 : 50); // Move more to the right
    const y = Math.max(rect.top - 20, isMobile ? 80 : 100); // Ensure minimum distance from top
    setBubblePosition({ x, y });

    // Show speech bubble
    setShowSpeechBubble(true);

    // Hide speech bubble after 3 seconds
    setTimeout(() => {
      setShowSpeechBubble(false);
    }, 3000);
  };

  const handleShopClick = () => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-1047967012/9GnhCNaIzIAcEKTq2vMD'
      });
      console.log('Conversion tracking event sent (Shop the Freakn Collection).');
    }
  };

  useEffect(() => {
    // Check if user is returning from successful payment
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');

    if (paymentStatus === 'success') {
      setShowSuccessMessage(true);
      // Remove the payment parameter from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      // Hide success message after 10 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 10000);
    }

    // Test first audio file accessibility
    if (musicFiles.length > 0) {
      testAudioFile(musicFiles[0]);
    }
  }, [musicFiles]);

  const isMobile = window.innerWidth <= 768;

  const floatingChickens = isMobile ? [
    // Mobile-optimized positions
    { src: '/assets/transparentBandana.png', x: 5, y: 15, delay: 0 },
    { src: '/assets/transparentBikini.png', x: 85, y: 10, delay: 1 },
    { src: '/assets/transparentChristmas.png', x: 15, y: 75, delay: 2 },
    { src: '/assets/transparentFRIEDCHICKEN.png', x: 75, y: 65, delay: 3 },
  ] : [
    // Desktop positions
    { src: '/assets/transparentBandana.png', x: 10, y: 20, delay: 0 },
    { src: '/assets/transparentBikini.png', x: 80, y: 10, delay: 1 },
    { src: '/assets/transparentChristmas.png', x: 20, y: 80, delay: 2 },
    { src: '/assets/transparentFRIEDCHICKEN.png', x: 70, y: 70, delay: 3 },
  ];

  return (
    <HeroSection id="home">
      {/* AUDIO PLAYER - COMMENTED OUT FOR FUTURE USE */}
      {/* 
      <HangingBanner
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3, type: "spring", damping: 15 }}
        onClick={handleBannerClick}
        style={{ cursor: 'pointer' }}
      >
        <BannerRibbon>
          <MusicNote>🎵</MusicNote>
        </BannerRibbon>
      </HangingBanner>

      <AnimatePresence>
        {showAudioPlayer && (
          <AudioPlayerPopup
            initial={{ opacity: 0, y: -20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.8 }}
            transition={{ duration: 0.3, type: "spring", damping: 15 }}
          >
            <TrackInfo>
              {musicFiles[currentTrackIndex].split('/').pop().replace('.mp3', '')}
            </TrackInfo>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)', textAlign: 'center', marginBottom: '0.5rem' }}>
              Track {currentTrackIndex + 1} of {musicFiles.length}
            </div>
            <AudioControls>
              <ControlButton
                onClick={togglePlayPause}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {isPlaying ? '⏸️' : '▶️'}
              </ControlButton>
              <ControlButton
                onClick={playNextTrack}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                ⏭️
              </ControlButton>
            </AudioControls>
            <HiddenAudio
              ref={setAudioRef}
              src={musicFiles[currentTrackIndex]}
              onEnded={handleAudioEnded}
              onLoadedData={handleAudioLoaded}
              onError={handleAudioError}
              onCanPlay={() => console.log('Audio can play:', musicFiles[currentTrackIndex])}
              onLoadStart={() => console.log('Audio load started:', musicFiles[currentTrackIndex])}
              preload="metadata"
              controls={false}
              playsInline={true}
              webkit-playsinline="true"
              crossOrigin="anonymous"
            />
          </AudioPlayerPopup>
        )}
      </AnimatePresence>
      */}

      <FloatingChickens>
        {floatingChickens.map((chicken, index) => (
          <FloatingChicken
            key={index}
            src={chicken.src}
            alt="Floating Chicken"
            style={{
              left: `${chicken.x}%`,
              top: `${chicken.y}%`,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 5, -5, 0],
            }}
            transition={{
              duration: 3,
              delay: chicken.delay,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </FloatingChickens>

      <HeroContent>
        {showSuccessMessage && (
          <SuccessMessage
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
          >
            <h3>🎉 Payment Successful!</h3>
            <p>Thank you for your order! You'll receive a confirmation email shortly.</p>
          </SuccessMessage>
        )}

        <HeroLogo
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          whileHover={{
            scale: 1.1,
            rotate: [0, -5, 5, 0]
          }}
          onClick={handleChickenClick}
          style={{ cursor: 'pointer' }}
        >
          <img src="/assets/transparentFreakn'chikin.png" alt="Chikin Lady Main Logo" />
        </HeroLogo>

        {showSpeechBubble && (
          <SpeechBubble
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 20 }}
            transition={{ duration: 0.3 }}
            style={{
              left: `${bubblePosition.x}px`,
              top: `${bubblePosition.y}px`,
              transform: 'translateX(-30%)'
            }}
          >
            {currentSaying}
          </SpeechBubble>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <CTAButton
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowProductCollection(true);
              handleShopClick();
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            🛒 Shop the Freakn' Collection
          </CTAButton>

          <CTAButton
            href="#products"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ background: 'linear-gradient(45deg, #6bcf7f, #4ecdc4)' }}
          >
            🐔 Meet the Cluckin' Ladies
          </CTAButton>
        </motion.div>

        <EtsyBadgeWrapper
          href="https://chikinlady.etsy.com"
          target="_blank"
          rel="noopener noreferrer"
          title="Visit Chikin Lady on Etsy"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          whileHover={{ scale: 1.05, y: -4 }}
          whileTap={{ scale: 0.96 }}
        >
          <EtsyBadgeImg
            src="/assets/etsy-chikin-badge.png"
            alt="Chikin Lady Etsy Shop"
          />
          <EtsyBadgeLabel>
            Visit Our Etsy Shop! 🛍️
          </EtsyBadgeLabel>
        </EtsyBadgeWrapper>
      </HeroContent>

      <ProductCollectionOverlay
        isOpen={showProductCollection}
        onClose={() => setShowProductCollection(false)}
      />
    </HeroSection>
  );
};

export default Hero;
